import { Link } from "react-router-dom";
import { Download } from "lucide-react";
import { t } from "../../i18n";
import { OrganizationEditor } from "../../components/organization/OrganizationEditor";
import { OrganizationPermissionsEditor } from "../../components/organization/OrganizationPermissionsEditor";
import { AuthenticationConnectionsEditor } from "../../components/integrations/AuthenticationConnectionsEditor";
import { OrganizationInviteForm } from "../../components/organization/OrganizationInviteForm";
import { OrganizationPricingEditor } from "../../components/organization/OrganizationPricingEditor";
import { OrganizationNetworkPolicyEditor } from "../../components/organization/OrganizationNetworkPolicyEditor";
import { OrganizationConsoleNav } from "../../components/organization/OrganizationConsoleNav";
import { OrganizationResourcesPanel } from "../../components/organization/OrganizationResourcesPanel";
import { Toggle } from "../../components/Toggle";
import {
  capabilityDefinitions,
  organizationResourceLabel,
  showLegacyOrganizationSections,
} from "./SiteOrganizationsPage.utils";
import type { Props } from "./SiteOrganizationEditContent.types";
import { SiteOrganizationAccessSection } from "./SiteOrganizationAccessSection";
import type {
  OrganizationEntitlements,
  OrganizationPricing,
  OrganizationResource,
  OrganizationResources,
  Row,
} from "./SiteOrganizationsPage.utils";

export function SiteOrganizationEditContent({
  activeTab,
  permissions,
  editing,
  savedEditing,
  navigate,
  saving,
  setEditing,
  saveOrganization,
  members,
  editMember,
  inviteMember,
  defaultPermissions,
  setDefaultPermissions,
  capabilities,
  saveDefaultPermissions,
  editingMember,
  setEditingMember,
  memberRole,
  setMemberRole,
  memberState,
  setMemberState,
  memberPermissions,
  setMemberPermissions,
  saveMember,
  authConnections,
  createAuthenticationConnection,
  updateNativePolicy,
  networkPolicy,
  savingNetworkPolicy,
  saveNetworkPolicy,
  setNetworkPolicy,
  integrations,
  editingIntegrationId,
  setEditingIntegrationId,
  editingIntegrationName,
  setEditingIntegrationName,
  renameIntegration,
  deleteIntegration,
  setIntegrationEnabled,
  billing,
  setBilling,
  saveBillingInformation,
  pricing,
  savingPricing,
  setPricing,
  savePricing,
  access,
  setProviderAccess,
  setModelAccess,
  setModelPolicy,
  resourceCatalog,
  entitlements,
  savingEntitlements,
  setAllToolsEnabled,
  savingCatalogResource,
  setCatalogAccess,
  toolSettings,
  openTool,
  setOpenToolId,
  setToolSettings,
  resetToolSettings,
  saveToolSettings,
  resources,
  user,
  setResourceEnabled,
  lifecycleBusy,
  changeLifecycle,
  audit,
  setExecutionEnabled,
  setOrganizationCapabilities,
  setEntitlements,
  saveEntitlements,
}: Props) {
  return (
    <>
      <div className="mb-6 space-y-6">
        <div className="flex items-start justify-between gap-4 rounded-xl border border-stone-200 bg-white p-4 dark:border-white/10 dark:bg-white/3">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="font-bold">{editing.name}</h2>
              <span className="rounded-full bg-stone-100 px-2 py-1 text-xs text-stone-600 dark:bg-white/10 dark:text-stone-300">
                {t("Site administration")}
              </span>
              {JSON.stringify(editing) !== JSON.stringify(savedEditing) && (
                <span className="rounded-full bg-amber-100 px-2 py-1 text-xs text-amber-800 dark:bg-amber-900/30 dark:text-amber-200">
                  {t("Unsaved changes")}
                </span>
              )}
            </div>
            <p className="mt-1 text-xs text-stone-500">
              {editing.slug} · {editing.state}
            </p>
          </div>
          <Link className="button" to="/settings/organizations">
            {t("All organizations")}
          </Link>
        </div>
        <OrganizationConsoleNav
          basePath={`/settings/organizations/${editing.id}`}
          activeTab={activeTab}
          dirty={JSON.stringify(editing) !== JSON.stringify(savedEditing)}
          groups={[
            {
              label: "Organization",
              items: [
                { key: "overview", label: "Overview" },
                { key: "members", label: "People" },
                { key: "resources", label: "Resources" },
              ],
            },
            {
              label: "Access & security",
              items: [
                { key: "access", label: "Access" },
                { key: "capabilities", label: "Capabilities" },
                { key: "security", label: "Network & security" },
                { key: "authentication", label: "Authentication" },
              ],
            },
            {
              label: "Operations",
              items: [{ key: "limits", label: "Limits" }],
            },
            {
              label: "Commercial & lifecycle",
              items: [
                { key: "billing", label: "Billing" },
                { key: "data", label: "Data & lifecycle" },
                { key: "audit", label: "Audit" },
              ],
            },
          ]}
        />
        {activeTab === "overview" &&
          permissions.includes("site:organizations:manage") && (
            <OrganizationEditor
              value={editing}
              saving={saving}
              siteFields
              onChange={(value) => setEditing(value as Row["organization"])}
              onSave={() => void saveOrganization()}
              onCancel={() => navigate("/settings/organizations")}
            />
          )}
        {activeTab === "members" &&
          permissions.includes("site:organization-users:manage") && (
            <section className="panel p-5">
              <h2 className="font-bold">{t("Organization members")}</h2>
              <p className="mt-1 text-sm text-stone-500">
                {t(
                  "Manage roles and organization-scoped permissions. These permissions never grant website-wide authority.",
                )}
              </p>
              <div className="mt-4 divide-y divide-stone-200 dark:divide-white/10">
                {members.map((member) => (
                  <div
                    key={member.user.id}
                    className="flex items-center justify-between gap-3 py-3"
                  >
                    <span>
                      <strong className="block text-sm">
                        {member.user.name}
                      </strong>
                      <span className="text-xs text-stone-500">
                        {member.user.email} · {member.membership.role} ·{" "}
                        {member.membership.state}
                      </span>
                    </span>
                    <button
                      className="button"
                      onClick={() => editMember(member)}
                    >
                      {t("Manage")}
                    </button>
                  </div>
                ))}
              </div>
              <OrganizationInviteForm onInvite={inviteMember} />
              <div className="mt-5 border-t border-stone-200 pt-4 dark:border-white/10">
                <h3 className="font-semibold">{t("Default permissions")}</h3>
                <div className="mt-3">
                  <OrganizationPermissionsEditor
                    value={defaultPermissions}
                    onChange={setDefaultPermissions}
                    personalProvidersEnabled={capabilities.personalProviders}
                  />
                </div>
                <div className="mt-3 flex justify-end">
                  <button
                    className="button-primary"
                    onClick={() => void saveDefaultPermissions()}
                  >
                    {t("Save default permissions")}
                  </button>
                </div>
              </div>
              {editingMember && (
                <div
                  id="site-member-permissions-editor"
                  className="mt-4 scroll-mt-24 rounded-xl border border-stone-200 p-4 dark:border-white/10 dark:bg-white/3"
                >
                  <h3 className="mb-3 font-semibold">
                    {t("Edit permissions for {name}").replace(
                      "{name}",
                      editingMember.user.name,
                    )}
                  </h3>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <select
                      className="field"
                      value={memberRole}
                      onChange={(event) => setMemberRole(event.target.value)}
                    >
                      <option value="owner">{t("Owner")}</option>
                      <option value="admin">{t("Administrator")}</option>
                      <option value="automation_manager">
                        {t("Automation manager")}
                      </option>
                      <option value="connection_manager">
                        {t("Connection manager")}
                      </option>
                      <option value="member">{t("Member")}</option>
                      <option value="billing_admin">
                        {t("Billing administrator")}
                      </option>
                    </select>
                    <select
                      className="field"
                      value={memberState}
                      onChange={(event) => setMemberState(event.target.value)}
                    >
                      <option value="invited">{t("Invited")}</option>
                      <option value="active">{t("Active")}</option>
                      <option value="suspended">{t("Suspended")}</option>
                    </select>
                  </div>
                  <div className="mt-4">
                    <OrganizationPermissionsEditor
                      value={memberPermissions}
                      onChange={setMemberPermissions}
                      personalProvidersEnabled={capabilities.personalProviders}
                    />
                  </div>
                  <div className="mt-4 flex justify-end gap-2">
                    <button
                      className="button"
                      onClick={() => setEditingMember(null)}
                    >
                      {t("Cancel")}
                    </button>
                    <button
                      className="button-primary"
                      onClick={() => void saveMember()}
                    >
                      {t("Save member")}
                    </button>
                  </div>
                </div>
              )}
            </section>
          )}
        {activeTab === "authentication" &&
          permissions.includes("site:organization-auth:manage") && (
            <AuthenticationConnectionsEditor
              connections={authConnections}
              onCreate={createAuthenticationConnection}
              onNativePolicyChange={(connection, policy) =>
                void updateNativePolicy(connection, policy)
              }
            />
          )}
        {activeTab === "security" && networkPolicy && (
          <OrganizationNetworkPolicyEditor
            policy={networkPolicy}
            saving={savingNetworkPolicy}
            onReset={() => void saveNetworkPolicy(null, null)}
            onChange={setNetworkPolicy}
            onSave={() =>
              void saveNetworkPolicy(
                networkPolicy.outboundHosts,
                networkPolicy.providerHosts,
              )
            }
          />
        )}
        {showLegacyOrganizationSections &&
          activeTab === "connections" &&
          permissions.includes("site:organization-access:manage") && (
            <section className="panel overflow-hidden">
              <div className="border-b border-stone-200 p-5 dark:border-white/10">
                <h2 className="font-bold">{t("Organization integrations")}</h2>
                <p className="mt-1 text-sm text-stone-500">
                  {t(
                    "These are the organization's business integrations—mailboxes, messaging, CRM, and other external services. Site administrators can manage the organization-owned configuration here or in the focused Integrations manager; member-owned personal integrations stay private.",
                  )}
                </p>
              </div>
              <div className="divide-y divide-stone-200 dark:divide-white/10">
                {integrations.map((integration) => (
                  <div
                    key={integration.id}
                    className="flex items-start justify-between gap-4 p-4"
                  >
                    <div>
                      {editingIntegrationId === integration.id ? (
                        <div className="flex flex-wrap gap-2">
                          <input
                            className="field py-1.5"
                            value={editingIntegrationName}
                            onChange={(event) =>
                              setEditingIntegrationName(event.target.value)
                            }
                            aria-label={t("Integration name")}
                          />
                          <button
                            className="button-primary text-xs"
                            onClick={() => void renameIntegration()}
                          >
                            {t("Save name")}
                          </button>
                          <button
                            className="button text-xs"
                            onClick={() => setEditingIntegrationId(null)}
                          >
                            {t("Cancel")}
                          </button>
                        </div>
                      ) : (
                        <strong className="block text-sm">
                          {integration.name}
                        </strong>
                      )}
                      <span className="text-xs text-stone-500">
                        {integration.kind} ·{" "}
                        {integration.capabilities.join(", ") ||
                          t("No declared capabilities")}
                      </span>
                      {integration.lastError && (
                        <span className="mt-1 block text-xs text-red-600">
                          {integration.lastError}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap justify-end gap-2">
                      <button
                        className={
                          integration.enabled ? "button" : "button-primary"
                        }
                        onClick={() =>
                          void setIntegrationEnabled(
                            integration,
                            !integration.enabled,
                          )
                        }
                      >
                        {integration.enabled ? t("Disable") : t("Enable")}
                      </button>
                      <button
                        className="button"
                        onClick={() => {
                          setEditingIntegrationId(integration.id);
                          setEditingIntegrationName(integration.name);
                        }}
                      >
                        {t("Rename")}
                      </button>
                      <button
                        className="button text-red-600"
                        onClick={() => void deleteIntegration(integration)}
                      >
                        {t("Delete")}
                      </button>
                    </div>
                  </div>
                ))}
                {!integrations.length && (
                  <p className="p-5 text-sm text-stone-500">
                    {t("No organization-owned connections configured.")}
                  </p>
                )}
              </div>
            </section>
          )}
        {showLegacyOrganizationSections &&
          activeTab === "automations" &&
          permissions.includes("site:organization-access:manage") && (
            <section className="panel overflow-hidden">
              <div className="border-b border-stone-200 p-5 dark:border-white/10">
                <h2 className="font-bold">{t("Organization automations")}</h2>
                <p className="mt-1 text-sm text-stone-500">
                  {t(
                    "Pause or resume organization automations here. Editing and publishing remain in the focused automation editor, so this page stays useful for oversight.",
                  )}
                </p>
              </div>
              <div className="divide-y divide-stone-200 dark:divide-white/10">
                {resources.automations.map((automation) => (
                  <div
                    key={automation.id}
                    className="flex items-start justify-between gap-4 p-4"
                  >
                    <div>
                      <strong className="block text-sm">
                        {automation.name}
                      </strong>
                      <span className="line-clamp-2 text-xs text-stone-500">
                        {automation.description || t("No description")}
                      </span>
                    </div>
                    <button
                      className={
                        automation.enabled ? "button" : "button-primary"
                      }
                      onClick={() =>
                        void setResourceEnabled(
                          "automations",
                          automation,
                          !automation.enabled,
                        )
                      }
                    >
                      {automation.enabled ? t("Pause") : t("Resume")}
                    </button>
                  </div>
                ))}
                {!resources.automations.length && (
                  <p className="p-5 text-sm text-stone-500">
                    {t("No organization automations configured.")}
                  </p>
                )}
              </div>
            </section>
          )}
        {activeTab === "data" && (
          <section className="panel p-5">
            <h2 className="font-bold">{t("Data and lifecycle")}</h2>
            <p className="mt-1 text-sm text-stone-500">
              {t(
                "Site administrators can export data and manage lifecycle state here. Destructive actions are delayed, resumable, and audited.",
              )}
            </p>
            <div className="mt-4 grid gap-3 sm:grid-cols-3">
              <div className="rounded-xl border border-stone-200 p-4 dark:border-white/10">
                <span className="text-xs tracking-wider text-stone-500 uppercase">
                  {t("State")}
                </span>
                <strong className="mt-1 block">{editing.state}</strong>
              </div>
              <div className="rounded-xl border border-stone-200 p-4 dark:border-white/10">
                <span className="text-xs tracking-wider text-stone-500 uppercase">
                  {t("Retention")}
                </span>
                <strong className="mt-1 block">
                  {t("days", { count: editing.retentionDays })}
                </strong>
              </div>
              <div className="rounded-xl border border-stone-200 p-4 dark:border-white/10">
                <span className="text-xs tracking-wider text-stone-500 uppercase">
                  {t("Deletion")}
                </span>
                <strong className="mt-1 block">
                  {editing.state === "cancelled"
                    ? t("Scheduled or cancelled")
                    : t("Not scheduled")}
                </strong>
              </div>
            </div>
            <div className="mt-5 flex flex-wrap gap-2">
              <a
                className="button"
                href={`/api/site/organizations/${editing.id}/export`}
              >
                <Download size={15} /> {t("Download organization export")}
              </a>
              {editing.state === "cancelled" ? (
                <button
                  className="button"
                  disabled={lifecycleBusy}
                  onClick={() => void changeLifecycle("recover")}
                >
                  {t("Recover organization")}
                </button>
              ) : (
                <button
                  className="button text-red-600"
                  disabled={lifecycleBusy}
                  onClick={() => void changeLifecycle("schedule")}
                >
                  {t("Schedule deletion")}
                </button>
              )}
            </div>
          </section>
        )}
        {activeTab === "audit" && (
          <section className="panel overflow-hidden">
            <div className="border-b border-stone-200 p-5 dark:border-white/10">
              <h2 className="font-bold">{t("Organization audit log")}</h2>
            </div>
            <div className="divide-y divide-stone-200 dark:divide-white/10">
              {audit.slice(0, 100).map((event) => (
                <div
                  key={event.id}
                  className="flex justify-between gap-3 p-3 text-sm"
                >
                  <span>
                    {event.action} · {event.targetType}
                  </span>
                  <time className="text-xs text-stone-500">
                    {new Date(event.createdAt).toLocaleString()}
                  </time>
                </div>
              ))}
              {!audit.length && (
                <p className="p-5 text-sm text-stone-500">
                  {t("No audit events found.")}
                </p>
              )}
            </div>
          </section>
        )}
        {activeTab === "capabilities" &&
          permissions.includes("site:organization-access:manage") && (
            <section className="panel p-5">
              <h2 className="font-bold">{t("Organization capabilities")}</h2>
              <p className="mt-1 text-sm text-stone-500">
                {t(
                  "These are site-controlled entitlements. High-risk network capabilities remain deny-by-default and require separate approval or a customer-managed connector.",
                )}
              </p>
              <div className="mt-5 rounded-xl border border-amber-300 bg-amber-50/60 p-4 dark:border-amber-700 dark:bg-amber-950/20">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="font-semibold">
                      {t("Organization execution")}
                    </h3>
                    <p className="mt-1 text-sm text-stone-600 dark:text-stone-300">
                      {t(
                        "Controls whether this organization may start new generations, automation runs, and other metered execution. Turn it off as an emergency kill switch or while setup is incomplete; it does not change numeric quotas.",
                      )}
                    </p>
                    <p className="mt-2 text-xs font-medium text-amber-800 dark:text-amber-300">
                      {entitlements.executionEnabled
                        ? t("Execution is enabled")
                        : t("Execution is disabled; new work will be blocked")}
                    </p>
                  </div>
                  <Toggle
                    checked={entitlements.executionEnabled}
                    disabled={savingEntitlements}
                    aria-label={t("Enable organization execution")}
                    onChange={(event) =>
                      void setExecutionEnabled(event.target.checked)
                    }
                  />
                </div>
              </div>
              <div className="mt-5 space-y-5">
                {(
                  [
                    "Standard",
                    "Enterprise approval",
                    "Connector required",
                  ] as const
                ).map((group) => (
                  <div key={group}>
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold">{t(group)}</h3>
                      {group !== "Standard" && (
                        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800 dark:bg-amber-900/30 dark:text-amber-200">
                          {t("Restricted")}
                        </span>
                      )}
                    </div>
                    <div className="mt-2 grid gap-3 lg:grid-cols-2">
                      {capabilityDefinitions
                        .filter((definition) => definition.group === group)
                        .map((definition) => {
                          const blocked =
                            definition.requires &&
                            !capabilities[definition.requires];
                          return (
                            <label
                              key={definition.key}
                              className={`flex items-start justify-between gap-4 rounded-xl border p-3 dark:bg-white/3 ${blocked ? "border-stone-200 opacity-60 dark:border-white/10" : definition.risk === "high" ? "border-amber-300 dark:border-amber-700" : "border-stone-200 dark:border-white/10"}`}
                            >
                              <span>
                                <strong className="block text-sm">
                                  {t(definition.title)}
                                </strong>
                                <span className="mt-1 block text-xs text-stone-500">
                                  {t(definition.description)}
                                </span>
                                {blocked && (
                                  <span className="mt-1 block text-xs text-amber-700 dark:text-amber-300">
                                    {t(
                                      "Enable the required connector capability first.",
                                    )}
                                  </span>
                                )}
                              </span>
                              <input
                                role="switch"
                                type="checkbox"
                                checked={capabilities[definition.key]}
                                disabled={Boolean(blocked)}
                                onChange={(event) => {
                                  if (
                                    event.target.checked &&
                                    definition.risk === "high" &&
                                    !window.confirm(
                                      t(
                                        "Enable this high-risk capability? Site approval and outbound policy controls still apply.",
                                      ),
                                    )
                                  )
                                    return;
                                  void setOrganizationCapabilities({
                                    ...capabilities,
                                    [definition.key]: event.target.checked,
                                  });
                                }}
                              />
                            </label>
                          );
                        })}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}
        {activeTab === "limits" &&
          permissions.includes("site:organization-access:manage") && (
            <section className="panel p-5">
              <h2 className="font-bold">
                {t("Limits and execution controls")}
              </h2>
              <p className="mt-1 text-sm text-stone-500">
                {t(
                  "These controls are enforced across API and worker replicas. Keep risky execution disabled until the organization is ready for production.",
                )}
              </p>
              <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {(
                  [
                    {
                      key: "maxSeats",
                      label: "Maximum seats",
                      unit: "seats",
                      description: "Maximum active members.",
                    },
                    {
                      key: "monthlyPromptLimit",
                      label: "Monthly prompt limit",
                      unit: "prompts",
                      description: "Set blank for unlimited.",
                    },
                    {
                      key: "monthlySpendMicros",
                      label: "Monthly spend limit",
                      unit: editing.currency ?? "currency",
                      description:
                        "Entered in normal currency units; metering stores micro-units.",
                    },
                    {
                      key: "maxConcurrentGenerations",
                      label: "Concurrent generations",
                      unit: "runs",
                      description:
                        "Across all API and worker replicas. Blank means unlimited.",
                    },
                    {
                      key: "maxConcurrentProviderDiscoveries",
                      label: "Provider discovery requests",
                      unit: "active",
                      description:
                        "Maximum active provider model-discovery requests across the organization. Blank means unlimited.",
                    },
                    {
                      key: "maxToolCallsPerGeneration",
                      label: "Tool calls per generation",
                      unit: "calls",
                      description:
                        "Maximum calls in one generation. Blank means unlimited.",
                    },
                    {
                      key: "maxSubagentFanout",
                      label: "Subagent fanout",
                      unit: "agents",
                      description:
                        "Maximum parallel subagents. Blank means unlimited.",
                    },
                    {
                      key: "maxWorkspaceRuntimeSeconds",
                      label: "Workspace runtime",
                      unit: "seconds",
                      description:
                        "Maximum runtime for one workspace session. Blank means unlimited.",
                    },
                    {
                      key: "maxConcurrentSshConnections",
                      label: "SSH / Proxmox connections",
                      unit: "connections",
                      description:
                        "Maximum active SSH or Proxmox connections for this organization. Blank means unlimited; host and capability policy still applies.",
                    },
                    {
                      key: "maxSshConnectionAttemptsPerMinute",
                      label: "SSH connection attempts",
                      unit: "per minute",
                      description:
                        "Rolling organization-wide limit for SSH and Proxmox connection tests and sessions. Blank means unlimited.",
                    },
                    {
                      key: "storageLimitBytes",
                      label: "Storage limit",
                      unit: "GB",
                      description: "Blank means unlimited.",
                    },
                  ] as {
                    key: keyof OrganizationEntitlements;
                    label: string;
                    unit: string;
                    description: string;
                  }[]
                ).map(({ key, label, unit, description }) => {
                  const displayValue =
                    entitlements[key] == null
                      ? ""
                      : key === "monthlySpendMicros"
                        ? Number(entitlements[key]) / 1_000_000
                        : key === "storageLimitBytes"
                          ? Number(entitlements[key]) / 1_000_000_000
                          : entitlements[key];
                  return (
                    <label key={key}>
                      <span className="label">
                        {t(label)} ({unit})
                      </span>
                      <input
                        className="field"
                        type="number"
                        min={
                          key === "maxSeats" ||
                          key === "monthlyPromptLimit" ||
                          key === "monthlySpendMicros" ||
                          key === "storageLimitBytes"
                            ? 0
                            : 1
                        }
                        step={
                          key === "monthlySpendMicros"
                            ? "0.01"
                            : key === "storageLimitBytes"
                              ? "0.1"
                              : "1"
                        }
                        placeholder={
                          key === "maxConcurrentGenerations" ||
                          key === "maxConcurrentProviderDiscoveries" ||
                          key === "maxToolCallsPerGeneration" ||
                          key === "maxSubagentFanout" ||
                          key === "maxWorkspaceRuntimeSeconds" ||
                          key === "maxConcurrentSshConnections" ||
                          key === "maxSshConnectionAttemptsPerMinute"
                            ? t("Unlimited")
                            : undefined
                        }
                        value={displayValue as number | string}
                        onChange={(event) =>
                          setEntitlements({
                            ...entitlements,
                            [key]:
                              event.target.value === ""
                                ? null
                                : key === "monthlySpendMicros"
                                  ? Math.round(
                                      Number(event.target.value) * 1_000_000,
                                    )
                                  : key === "storageLimitBytes"
                                    ? Math.round(
                                        Number(event.target.value) *
                                          1_000_000_000,
                                      )
                                    : Number(event.target.value),
                          })
                        }
                      />
                      <span className="mt-1 block text-xs text-stone-500">
                        {t(description)}
                      </span>
                    </label>
                  );
                })}
              </div>
              <div className="mt-4 flex justify-end">
                <button
                  className="button-primary"
                  disabled={savingEntitlements}
                  onClick={() => void saveEntitlements()}
                >
                  {t("Save limits")}
                </button>
              </div>
            </section>
          )}
        <SiteOrganizationAccessSection
          activeTab={activeTab}
          permissions={permissions}
          access={access}
          setProviderAccess={setProviderAccess}
          setModelAccess={setModelAccess}
          setModelPolicy={setModelPolicy}
          resourceCatalog={resourceCatalog}
          entitlements={entitlements}
          savingEntitlements={savingEntitlements}
          setAllToolsEnabled={setAllToolsEnabled}
          savingCatalogResource={savingCatalogResource}
          setCatalogAccess={setCatalogAccess}
          toolSettings={toolSettings}
          openTool={openTool}
          setOpenToolId={setOpenToolId}
          setToolSettings={setToolSettings}
          resetToolSettings={resetToolSettings}
          saveToolSettings={saveToolSettings}
        />
        {activeTab === "billing" &&
          permissions.includes("site:organization-billing:manage") && (
            <section className="panel p-5">
              <h2 className="font-bold">{t("Billing information")}</h2>
              <div className="mt-4 grid gap-3 sm:grid-cols-3">
                <input
                  className="field"
                  placeholder={t("Legal name")}
                  value={billing.legalName ?? ""}
                  onChange={(event) =>
                    setBilling({ ...billing, legalName: event.target.value })
                  }
                />
                <input
                  className="field"
                  type="email"
                  placeholder={t("Billing email")}
                  value={billing.billingEmail ?? ""}
                  onChange={(event) =>
                    setBilling({
                      ...billing,
                      billingEmail: event.target.value,
                    })
                  }
                />
                <input
                  className="field"
                  placeholder={t("Tax ID")}
                  value={billing.taxId ?? ""}
                  onChange={(event) =>
                    setBilling({ ...billing, taxId: event.target.value })
                  }
                />
              </div>
              <div className="mt-3 flex justify-end">
                <button
                  className="button-primary"
                  disabled={!billing.legalName || !billing.billingEmail}
                  onClick={() => void saveBillingInformation()}
                >
                  {t("Save billing information")}
                </button>
              </div>
            </section>
          )}
        {activeTab === "billing" &&
          permissions.includes("site:pricing:manage") &&
          pricing && (
            <OrganizationPricingEditor
              value={pricing}
              saving={savingPricing}
              onChange={(next) => setPricing(next as OrganizationPricing)}
              onSave={() => void savePricing()}
            />
          )}
        {activeTab === "resources" &&
          permissions.includes("site:organization-access:manage") && (
            <div className="space-y-6">
              {editing && (
                <OrganizationResourcesPanel
                  permissions={permissions}
                  capabilities={capabilities}
                  organizationId={editing.id}
                  siteOrganizationManagement
                  user={user}
                />
              )}
              {(
                Object.entries(resources) as [
                  keyof OrganizationResources,
                  OrganizationResource[],
                ][]
              ).map(([type, items]) => (
                <section key={type} className="panel p-5">
                  <h2 className="font-bold">
                    {organizationResourceLabel(type)}
                  </h2>
                  <div className="mt-3 divide-y divide-stone-200 dark:divide-white/10">
                    {items.map((resource) => (
                      <label
                        key={resource.id}
                        className="flex items-start justify-between gap-4 py-3"
                      >
                        <span>
                          <strong className="block text-sm">
                            {resource.name}
                          </strong>
                          {resource.description && (
                            <span className="line-clamp-2 text-xs text-stone-500">
                              {resource.description}
                            </span>
                          )}
                        </span>
                        <input
                          role="switch"
                          type="checkbox"
                          checked={resource.enabled}
                          onChange={(event) =>
                            void setResourceEnabled(
                              type,
                              resource,
                              event.target.checked,
                            )
                          }
                        />
                      </label>
                    ))}
                    {!items.length && (
                      <p className="py-3 text-sm text-stone-500">
                        {t("No organization resources of this type.")}
                      </p>
                    )}
                  </div>
                </section>
              ))}
            </div>
          )}
      </div>
    </>
  );
}
